import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';

import { FormSkeleton } from 'components/Shared/skeletons';
import { toSentenceCase } from 'utils';

import { useTransl } from 'shared/lib/i18n';

import GenericFormContainer from '../Forms/GenericFormContainer';

import {
  confirmExternalPaymentsUrl,
  createExternalAccount,
  loadExternalAccountById,
  updateExternalAccount,
} from './actions';

import { getIsLoading } from '../Shared/selectors';
import { getExternalAccountById, getExternalAccountSchema } from './selectors';

import {
  generateFormFieldsFromSchema,
  getFormInitialValues,
} from '../Forms/schemaUtils';

export const ExternalAccountForm = () => {
  const { t } = useTransl();
  const history = useHistory();
  const dispatch = useDispatch();
  const { type, id } = useParams();

  const isUpdate = id !== 'create';

  const isLoading = useSelector(getIsLoading);
  const accountSchema = useSelector(getExternalAccountSchema(type));
  const externalAccount = useSelector(getExternalAccountById(id));

  useEffect(() => {
    isUpdate && loadExternalAccountById(dispatch, type, id);
  }, []);

  if (isLoading && (!accountSchema || (isUpdate && !externalAccount))) {
    return <FormSkeleton />;
  }

  if (isUpdate && !externalAccount) {
    return t('externalAccounts.accountNotFound');
  }

  const initialValues = getFormInitialValues(
    accountSchema,
    externalAccount,
    isUpdate,
  );

  const formData = generateFormFieldsFromSchema(accountSchema, isUpdate);

  const title = toSentenceCase(`${type} - ${externalAccount?.name}`);

  const onSubmit = async (data) => {
    if (type === 'external' && initialValues?.url !== data?.url) {
      confirmExternalPaymentsUrl(dispatch, type, id, data, isUpdate);
      return;
    }
    const res = isUpdate
      ? await updateExternalAccount(dispatch, type, id, data)
      : await createExternalAccount(dispatch, type, data);
    if (res?.data) history.goBack();
  };

  return (
    <GenericFormContainer
      form={type}
      formData={formData}
      initialValues={initialValues}
      onSubmit={onSubmit}
      submitText={isUpdate ? t('actions.update') : t('actions.create')}
      title={title}
      enableReinitialize
    />
  );
};
